/* eslint-disable */
import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  decideLegacy,
  ensureLegacyDecision,
  initLegacySmartNav,
  isLegacyPreloadEnabled,
  markSettled,
  redirectToShell,
  toShellUrl,
} from './legacyNav';

type NavigateOptions = { replace?: boolean; state?: any };

const SMART_NAV_ENABLED = process.env.REACT_APP_SMART_NAV_ENABLED === 'true';
// Flag-aware mode: only bounce routes the shell actually owns+activated; legacy routes stay
// native (no legacy->shell->legacy round-trip). Off => unconditional bounce (legacy behaviour).
const PRELOAD = isLegacyPreloadEnabled();

// Kick off Unleash polling + registry fetch as early as possible in flag-aware mode.
if (SMART_NAV_ENABLED && PRELOAD) {
  initLegacySmartNav();
}

/** True when the shell owns+activated this path (so we should bounce to the shell). */
function shouldBounce(path: string): boolean {
  return decideLegacy(path) === 'shell';
}

// Hard-redirect a click target to the shell. In flag-aware mode only bounces shell-owned
// routes; returns true when it handled the event.
export function smartRedirect(e: React.MouseEvent, path: string): boolean {
  if (!SMART_NAV_ENABLED) return false;
  if (PRELOAD) {
    if (!shouldBounce(path)) return false; // legacy owns it → let native nav happen
    e.preventDefault();
    redirectToShell(path, true);
    return true;
  }
  e.preventDefault();
  window.location.replace(toShellUrl(path, true));
  return true;
}

// Intercepts anchor clicks (capture phase). In flag-aware mode only bounces shell-owned routes.
export function useSmartNavInterceptor(): void {
  useEffect(() => {
    if (!SMART_NAV_ENABLED) return;

    const handleCapture = (e: MouseEvent): void => {
      const link = (e.target as Element).closest('a[href]') as HTMLAnchorElement | null;
      if (!link) return;
      const href = link.getAttribute('href');
      if (
        !href ||
        href === '#' ||
        href.startsWith('http') ||
        href.startsWith('mailto:') ||
        href.startsWith('//')
      ) {
        return;
      }
      const normalizedPath = href.startsWith('/') ? href : '/' + href;
      if (PRELOAD && !shouldBounce(normalizedPath)) return; // legacy owns it → native nav
      e.preventDefault();
      e.stopImmediatePropagation();
      if (PRELOAD) redirectToShell(normalizedPath);
      else window.location.replace(toShellUrl(normalizedPath));
    };

    document.addEventListener('click', handleCapture, true);
    return () => document.removeEventListener('click', handleCapture, true);
  }, []);
}

// Drop-in replacement for react-router's useNavigate. In flag-aware mode only shell-owned
// targets bounce; everything else navigates natively in the legacy.
export function useSmartNavigateLegacy() {
  const navigate = useNavigate();

  return (to: string, options?: NavigateOptions) => {
    if (SMART_NAV_ENABLED && typeof to === 'string') {
      if (PRELOAD) {
        if (shouldBounce(to) && redirectToShell(to, true)) return;
        navigate(to, options);
        return;
      }
      window.location.replace(toShellUrl(to, true));
      return;
    }
    navigate(to, options);
  };
}

// True once the boot gate has decided the current document load.
let _bootGateRan = false;

// Boot gate (flag-aware mode): resolve the verdict for the current URL *before* React mounts, so
// a shell-owned URL never paints the legacy page first — that paint is the visible first hop of
// the legacy->shell "double jump". The static #preloader in index.html stays up for the whole
// wait, which ensureLegacyDecision bounds with the fail-toward-legacy timeout.
// Returns true when the legacy should render, false when a redirect to the shell is under way.
export async function resolveLegacyBoot(): Promise<boolean> {
  if (!SMART_NAV_ENABLED || !PRELOAD) return true;
  _bootGateRan = true;
  const { pathname } = window.location;
  // A rejected decision must never leave the app unmounted behind the preloader: fail toward
  // legacy and render, same as the timeout path inside ensureLegacyDecision.
  const verdict = await ensureLegacyDecision(pathname).catch(() => 'legacy' as const);
  // redirectToShell returns false when the loop guard trips → render in place instead.
  if (verdict === 'shell' && redirectToShell(pathname, true)) return false;
  markSettled(); // rendered in place (or loop guard tripped) → reset the counter
  return true;
}

// On-load gate (flag-aware mode): if the current URL is actually a shell-owned route,
// bounce to the shell for canonical entry; otherwise render the legacy in place. Mount
// once, globally (e.g. in RoutesBuilder), alongside useSmartNavInterceptor.
//
// Defense-in-depth only: resolveLegacyBoot() normally decides this same URL before React mounts.
// Once it has run we skip — re-deciding after the first paint is exactly the post-render bounce
// the boot gate exists to prevent.
export function useLegacyLoadGate(): void {
  useEffect(() => {
    if (!SMART_NAV_ENABLED || !PRELOAD || _bootGateRan) return;
    let cancelled = false;
    void ensureLegacyDecision(window.location.pathname).then((verdict) => {
      if (cancelled) return;
      if (verdict === 'shell') {
        const { pathname } = window.location;
        if (redirectToShell(pathname, true)) return;
      }
      markSettled(); // rendered in place (or loop guard tripped) → reset the counter
    });
    return () => { cancelled = true; };
  }, []);
}
